/**
 * MarketSizeWidget — Widget contextuel "Taille de marché" pour les 4 modules :
 *   Origination, Financement, Courtage, Immobilier.
 *
 * Affiche les tailles local/Suisse/Europe/Monde, la décomposition par rôle,
 * la part de marché actuelle vs atteignable 12m/36m, un narrative actionnable
 * et un indicateur de fiabilité par métrique.
 *
 * Pattern : check cache → POST /compute/start → polling jobs.
 * Mode additif : à monter en haut de chaque page module, ne casse rien si Tavily off.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import {
  TrendingUp,
  RefreshCw,
  Loader2,
  Globe,
  Target,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Info,
} from "lucide-react";

function fmtMoney(value, unit) {
  if (value == null) return "—";
  const n = Number(value);
  if (!isFinite(n)) return "—";
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T ${unit || ""}`.trim();
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}Md ${unit || ""}`.trim();
  if (n >= 1e6) return `${(n / 1e6).toFixed(0)}M ${unit || ""}`.trim();
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}k ${unit || ""}`.trim();
  return `${n} ${unit || ""}`.trim();
}

function fmtPct(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (n < 0.01) return `${(n).toFixed(4)}%`;
  if (n < 1) return `${(n).toFixed(2)}%`;
  return `${(n).toFixed(1)}%`;
}

function ReliabilityPill({ kind }) {
  const map = {
    estimated: { label: "estimé", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30" },
    public_source: { label: "source publique", cls: "bg-[#378ADD]/10 text-[#378ADD] border-[#378ADD]/30" },
    verifiable: { label: "vérifiable", cls: "bg-[#1D9E75]/10 text-[#1D9E75] border-[#1D9E75]/30" },
  };
  const v = map[kind] || map.estimated;
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded border ${v.cls}`} title={kind}>
      {v.label}
    </span>
  );
}

export default function MarketSizeWidget({ moduleKey, title }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ cached: false, stale: false, computed_at: null });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [expanded, setExpanded] = useState(false);

  const loadCached = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/market/size/${moduleKey}`);
      if (r.data?.cached || r.data?.stale) {
        setData(r.data?.data || null);
        setMeta({
          cached: !!r.data?.cached,
          stale: !!r.data?.stale,
          computed_at: r.data?.computed_at,
        });
      }
    } catch {
      /* ignore — affichera CTA compute */
    }
  }, [moduleKey]);

  useEffect(() => {
    loadCached();
  }, [loadCached]);

  const compute = async (forceRefresh = false) => {
    setBusy(true);
    setProgress(t("saas.market_size.starting", { defaultValue: "Démarrage…" }));
    try {
      const startRes = await api.post(`/workspace/market/size/compute/start`, {
        module_key: moduleKey,
        force_refresh: forceRefresh,
      });
      if (startRes.data?.from_cache) {
        setData(startRes.data.data || null);
        setMeta({ cached: true, stale: false, computed_at: startRes.data.computed_at });
        setBusy(false);
        return;
      }
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("no job_id");
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await api.get(`/workspace/market/size/compute/jobs/${jobId}`);
        setProgress(poll.data?.progress || "");
        if (poll.data?.status === "done") {
          setData(poll.data.data || null);
          setMeta({ cached: true, stale: false, computed_at: poll.data.computed_at });
          setBusy(false);
          notify.success(t("saas.market_size.done", { defaultValue: "Analyse marché terminée." }));
          return;
        }
        if (poll.data?.status === "failed") {
          throw new Error(poll.data?.error || "job failed");
        }
      }
      throw new Error("polling timeout");
    } catch (e) {
      notify.error(fmtError(e, t("saas.market_size.failed", { defaultValue: "Analyse marché impossible." })));
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  // === Empty state (no data, not yet computed) ===
  if (!data) {
    return (
      <div className="noam-card p-5" data-testid={`market-size-${moduleKey}-empty`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="noam-overline inline-flex items-center gap-1.5">
              <Globe className="w-3 h-3 text-[#378ADD]" />
              {t("saas.market_size.title", { defaultValue: "Taille de marché" })}
            </div>
            <div className="text-sm text-zinc-300 mt-2">
              {title || moduleKey}
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">
              {t("saas.market_size.empty_help", { defaultValue: "Estime la taille de marché adressable via recherche web + NOAM. ~10s." })}
            </p>
          </div>
          <button
            onClick={() => compute(false)}
            disabled={busy}
            className="btn-primary text-xs whitespace-nowrap"
            data-testid={`market-size-${moduleKey}-compute-btn`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
            {busy ? (progress || t("saas.market_size.computing", { defaultValue: "Analyse…" })) : t("saas.market_size.compute", { defaultValue: "Lancer l'analyse" })}
          </button>
        </div>
      </div>
    );
  }

  // === Loaded state ===
  const sizes = data.sizes || {};
  const sizesArr = ["local", "swiss", "europe", "world"].map((k) => ({ key: k, ...(sizes[k] || {}) }));
  const breakdown = data.role_breakdown || [];

  return (
    <div className="noam-card p-5" data-testid={`market-size-${moduleKey}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="noam-overline inline-flex items-center gap-1.5">
            <Globe className="w-3 h-3 text-[#378ADD]" />
            {t("saas.market_size.title", { defaultValue: "Taille de marché" })}
            {meta.stale && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30" title="Données > 7j — pense à régénérer">
                {t("saas.market_size.stale", { defaultValue: "obsolète" })}
              </span>
            )}
          </div>
          <div className="text-sm text-white font-medium mt-1">{title || moduleKey}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setExpanded(!expanded)}
            className="btn-ghost text-[11px]"
            data-testid={`market-size-${moduleKey}-toggle`}
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? t("saas.market_size.collapse", { defaultValue: "Replier" }) : t("saas.market_size.expand", { defaultValue: "Détails" })}
          </button>
          <button
            onClick={() => compute(true)}
            disabled={busy}
            className="btn-ghost text-[11px]"
            title={t("saas.market_size.regen", { defaultValue: "Régénérer (force refresh)" })}
            data-testid={`market-size-${moduleKey}-regen-btn`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {busy ? (progress || "…") : t("saas.market_size.regen_short", { defaultValue: "Régénérer" })}
          </button>
        </div>
      </div>

      {/* Sizes grid 4 cols */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-3">
        {sizesArr.map((s) => (
          <div key={s.key} className="bg-white/[0.03] border border-white/5 rounded-lg p-2.5" data-testid={`market-size-${moduleKey}-${s.key}`}>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label || s.key}</div>
            <div className="text-sm font-bold text-white mt-0.5">{fmtMoney(s.value, s.unit)}</div>
            <div className="mt-1.5">
              {s.reliability && <ReliabilityPill kind={s.reliability} />}
            </div>
          </div>
        ))}
      </div>

      {/* Current share */}
      <div className="grid grid-cols-3 gap-2.5 mb-3">
        <div className="bg-white/[0.03] border border-white/5 rounded-lg p-2.5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("saas.market_size.current_share", { defaultValue: "Part actuelle" })}</div>
          <div className="text-lg font-bold text-[#378ADD] mt-0.5">{fmtPct(data.current_share_pct)}</div>
          {data.current_share_value != null && (
            <div className="text-[10px] text-zinc-500">{fmtMoney(data.current_share_value, data.currency_primary)}</div>
          )}
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-lg p-2.5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("saas.market_size.achievable_12m", { defaultValue: "Atteignable 12m" })}</div>
          <div className="text-lg font-bold text-[#EF9F27] mt-0.5">{fmtPct(data.achievable_12m_pct)}</div>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-lg p-2.5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("saas.market_size.achievable_36m", { defaultValue: "Atteignable 36m" })}</div>
          <div className="text-lg font-bold text-[#1D9E75] mt-0.5">{fmtPct(data.achievable_36m_pct)}</div>
        </div>
      </div>

      {/* Narrative */}
      {data.narrative && (
        <div className="bg-[#378ADD]/5 border border-[#378ADD]/20 rounded-lg p-3 text-xs text-zinc-300 leading-relaxed mb-2" data-testid={`market-size-${moduleKey}-narrative`}>
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-[#378ADD] shrink-0 mt-0.5" />
            <span className="whitespace-pre-wrap">{data.narrative}</span>
          </div>
        </div>
      )}

      {/* Expanded section: role breakdown + sources */}
      {expanded && (
        <div className="space-y-3 pt-3 border-t border-white/5">
          {breakdown.length > 0 && (
            <div>
              <div className="noam-overline mb-2">
                <Target className="w-3 h-3 inline mr-1" />
                {t("saas.market_size.role_breakdown", { defaultValue: "Décomposition par rôle métier" })}
              </div>
              <div className="space-y-1.5">
                {breakdown.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs px-2.5 py-1.5 rounded bg-white/[0.02] border border-white/5">
                    <div className="text-zinc-200 truncate">{r.role}</div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-zinc-400">{fmtPct(r.share_of_market_pct)}</span>
                      <span className="text-[10px] text-zinc-500">·</span>
                      <span className="text-zinc-300">{fmtMoney(r.addressable_value, r.unit || data.currency_primary)}</span>
                      {r.reliability && <ReliabilityPill kind={r.reliability} />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(data.sources_cited || []).length > 0 && (
            <div>
              <div className="noam-overline mb-2">{t("saas.market_size.sources", { defaultValue: "Sources citées" })}</div>
              <div className="space-y-1">
                {(data.sources_cited || []).slice(0, 6).map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-[#378ADD] hover:text-white truncate block"
                    data-testid={`market-size-${moduleKey}-src-${i}`}
                  >
                    {url}
                  </a>
                ))}
              </div>
            </div>
          )}
          {meta.computed_at && (
            <div className="text-[10px] text-zinc-600 inline-flex items-center gap-1">
              <CheckCircle2 className="w-2.5 h-2.5" />
              {t("saas.market_size.computed_at", { defaultValue: "Calculé le" })} {new Date(meta.computed_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
